import { useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface SortableItem {
  id: string;
}

export interface SortableListProps<T extends SortableItem> {
  items: T[];
  /** Called with the new ordered ids after a successful drag. */
  onReorder: (nextIds: string[]) => void | Promise<void>;
  /** Renderer for a single row. Receives drag-handle props for opt-in handles. */
  renderItem: (item: T, helpers: { dragHandle: ReactNode }) => ReactNode;
  className?: string;
  /** When true the entire row becomes the drag handle. */
  wholeRowDrag?: boolean;
  disabled?: boolean;
}

/**
 * Keyboard-accessible vertical sortable list used by Tables, Categories,
 * Products-in-category and Links. The caller owns the data — this component
 * only reports the new order.
 */
export function SortableList<T extends SortableItem>({
  items,
  onReorder,
  renderItem,
  className,
  wholeRowDrag = false,
  disabled = false,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = useMemo(() => items.map((i) => i.id), [items]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = ids.indexOf(String(active.id));
      const newIndex = ids.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;
      const next = arrayMove(ids, oldIndex, newIndex);
      void onReorder(next);
    },
    [ids, onReorder],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ul className={cn('flex flex-col gap-2', className)}>
          {items.map((item) => (
            <SortableRow
              key={item.id}
              id={item.id}
              disabled={disabled}
              wholeRowDrag={wholeRowDrag}
            >
              {(dragHandle) => renderItem(item, { dragHandle })}
            </SortableRow>
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({
  id,
  disabled,
  wholeRowDrag,
  children,
}: {
  id: string;
  disabled: boolean;
  wholeRowDrag: boolean;
  children: (dragHandle: ReactNode) => ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  } as const;

  const handle = (
    <button
      type="button"
      className={cn(
        'inline-flex h-7 w-7 cursor-grab items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 active:cursor-grabbing dark:hover:bg-gray-800 dark:hover:text-gray-300',
        disabled && 'cursor-not-allowed opacity-40',
      )}
      aria-label="Reorder row"
      disabled={disabled}
      {...attributes}
      {...listeners}
    >
      <GripVertical className="h-4 w-4" aria-hidden="true" />
    </button>
  );

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        'list-none',
        isDragging && 'z-10 opacity-70 shadow-lg',
      )}
      {...(wholeRowDrag ? { ...attributes, ...listeners } : {})}
    >
      {children(handle)}
    </li>
  );
}
